import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { donationAPI } from '../lib/api';
import { Heart, Loader2, ArrowLeft, CheckCircle2, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function AdminDonationsPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) { navigate('/login'); return; }
    if (authLoading) return;
    loadData();
  }, [user, isAdmin, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [dRes, sRes] = await Promise.all([
        donationAPI.adminList({ status: filter }),
        donationAPI.adminStats()
      ]);
      setDonations(dRes.data.donations || []);
      setStats(sRes.data);
    } catch (err) {
      toast.error('Failed to load donations');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading && user && isAdmin) {
      setLoading(true);
      loadData();
    }
  }, [filter]);

  const handleUpdate = async (id) => {
    try {
      await donationAPI.adminUpdate(id, editData);
      toast.success('Donation updated');
      setEditingId(null);
      setEditData({});
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400',
    reviewed: 'bg-blue-500/10 text-blue-400',
    accepted: 'bg-green-500/10 text-green-400',
    transferred: 'bg-purple-500/10 text-purple-400',
    completed: 'bg-[#00E5FF]/10 text-[#00E5FF]',
    rejected: 'bg-red-500/10 text-red-400',
  };

  if (loading || authLoading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="admin-donations-page">
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="text-zinc-400 hover:text-white" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4 mr-1" /> Admin
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <Heart className="h-6 w-6 text-[#FF6B9D]" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">Domain Donations</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Total', value: stats.total || 0, icon: Heart, color: 'text-[#FF6B9D]' },
          { label: 'Pending', value: stats.pending || 0, icon: Clock, color: 'text-amber-400' },
          { label: 'Accepted', value: stats.accepted || 0, icon: CheckCircle2, color: 'text-green-400' },
          { label: 'Completed', value: stats.completed || 0, icon: CheckCircle2, color: 'text-[#00E5FF]' },
          { label: 'Est. Value', value: `$${(stats.total_estimated_value || 0).toLocaleString()}`, icon: Heart, color: 'text-[#00FF94]' },
        ].map((s, i) => (
          <div key={i} className="p-4 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <s.icon className={`h-4 w-4 ${s.color} mb-2`} />
            <div className="text-xl font-bold text-white font-['Outfit']">{s.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-3 mb-6">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="all" className="text-zinc-300">All Statuses</SelectItem>
            {['pending', 'reviewed', 'accepted', 'transferred', 'completed', 'rejected'].map(s => (
              <SelectItem key={s} value={s} className="text-zinc-300 capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Donations Table */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
        {donations.length === 0 ? (
          <div className="text-center py-16"><p className="text-zinc-500">No donations found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Donor</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Est. Value</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Recipient</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Actions</th>
              </tr></thead>
              <tbody>
                {donations.map(d => (
                  <React.Fragment key={d.id}>
                    <tr className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm text-white font-medium">{d.domain_name}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-white">{d.donor_name}</div>
                        <div className="text-xs text-zinc-500">{d.donor_email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-zinc-400">${(d.estimated_value || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500 capitalize">{d.intended_recipient || 'Any'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${statusColors[d.status] || 'bg-zinc-800 text-zinc-400'}`}>{d.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingId(editingId === d.id ? null : d.id);
                          setEditData({ status: d.status, admin_notes: d.admin_notes || '', estimated_value: d.estimated_value || 0 });
                        }} className="text-[#00E5FF] text-xs" data-testid={`edit-donation-${d.id}`}>
                          {editingId === d.id ? 'Close' : 'Manage'}
                        </Button>
                      </td>
                    </tr>
                    {editingId === d.id && (
                      <tr className="border-b border-white/5 bg-[#050505]">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                              <Select value={editData.status} onValueChange={(v) => setEditData(p => ({ ...p, status: v }))}>
                                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/10">
                                  {['pending', 'reviewed', 'accepted', 'transferred', 'completed', 'rejected'].map(s => (
                                    <SelectItem key={s} value={s} className="text-zinc-300 capitalize">{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Estimated Value ($)</Label>
                              <Input type="number" value={editData.estimated_value} onChange={(e) => setEditData(p => ({ ...p, estimated_value: e.target.value }))}
                                className="mt-1 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm" />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Admin Notes</Label>
                              <Textarea value={editData.admin_notes} onChange={(e) => setEditData(p => ({ ...p, admin_notes: e.target.value }))}
                                className="mt-1 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm resize-none" rows={2} />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button onClick={() => handleUpdate(d.id)} className="bg-[#00E5FF] text-black font-bold rounded-sm text-xs" data-testid={`save-donation-${d.id}`}>
                              Save Changes
                            </Button>
                            {d.message && <p className="text-xs text-zinc-500 mt-2 ml-3 self-center">Note: "{d.message}"</p>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
